import { defineCollection } from "@rebasepro/common";
import mailboxesCollection from "./mailboxes.js";

/**
 * Where the reader got to in each folder of each mailbox.
 *
 * IMAP identifies messages by a UID that is only meaningful within a given
 * `UIDVALIDITY` generation. If the server renumbers a folder — a restore, a
 * migration, some providers on a whim — UIDVALIDITY changes and every stored
 * UID becomes meaningless. Keeping both is what lets the reader notice that and
 * start over instead of silently skipping every message forever.
 *
 * Replaces `sync_state`, which keyed a cursor on the folder name alone and made
 * it unique — right for one mailbox, and wrong the moment a second one also has
 * an `INBOX`. The provisioner only ever adds, so that constraint could not be
 * relaxed in place; the table was replaced, and its rows were carried over to
 * the first mailbox by the migration in `inbox/mailboxes.ts`.
 *
 * `key` is `<mailbox id>:<role>` and is the unique thing. Delete a row to make
 * that folder be read again from `backfill_days` ago.
 */
const mailboxFoldersCollection = defineCollection({
    name: "Mailbox folders",
    singularName: "Mailbox folder",
    slug: "mailbox_folders",
    table: "mailbox_folders",
    description: "IMAP read cursors, one per folder per mailbox. Delete a row to re-read that folder.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },
        key: {
            name: "Key",
            type: "string",
            validation: { required: true, unique: true },
            description: "<mailbox id>:<role>",
            admin: { readOnly: true }
        },
        mailbox: {
            name: "Mailbox",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => mailboxesCollection,
                relationName: "mailbox",
                localKey: "mailbox_id",
                onDelete: "cascade"
            },
            validation: { required: true }
        },
        role: {
            name: "Role",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "inbox", label: "Inbox", color: "blue" },
                { id: "sent", label: "Sent", color: "green" }
            ]
        },
        path: { name: "Folder", type: "string", description: "The folder's name on the server" },
        uid_validity: {
            name: "UIDVALIDITY",
            type: "number",
            description: "The folder generation these UIDs belong to. A change means every stored UID is void."
        },
        last_uid: { name: "Last UID", type: "number", description: "Highest UID already read" },
        last_synced_at: { name: "Last read", type: "date" },
        last_error: { name: "Last error", type: "string", admin: { multiline: true } }
    },
    admin: {
        icon: "RefreshCw",
        group: "Diagnostics",
        defaultViewMode: "table",
        display: {
            title: "path",
            subtitle: "key",
            date: "last_synced_at"
        },
        propertiesOrder: ["mailbox", "role", "path", "last_synced_at", "last_uid", "uid_validity", "last_error", "key"]
    }
});

export default mailboxFoldersCollection;
